import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { Scale, Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = {
  legalEntityName: '',
  tradingName: 'Practice Manager Pro',
  abn: '',
  acn: '',
  registeredAddress: '',
  privacyEmail: '',
  supportEmail: '',
  governingLaw: '',
};

/** Mirrors the server-side check so the error shows before a round trip. */
function isValidAbn(abn) {
  const digits = String(abn || '').replace(/\s/g, '');
  if (!digits) return true;
  if (!/^\d{11}$/.test(digits)) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const values = digits.split('').map(Number);
  values[0] -= 1;
  return values.reduce((sum, d, i) => sum + d * weights[i], 0) % 89 === 0;
}

function formatAbn(abn) {
  const digits = String(abn || '').replace(/\s/g, '');
  if (!/^\d{11}$/.test(digits)) return abn || '';
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
}

const LegalEntityPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isConfigured, setIsConfigured] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getPlatformSettings();
      const settings = response.data?.settings;
      if (settings) {
        setForm({
          legalEntityName: settings.legalEntityName || '',
          tradingName: settings.tradingName || 'Practice Manager Pro',
          abn: settings.abn || '',
          acn: settings.acn || '',
          registeredAddress: settings.registeredAddress || '',
          privacyEmail: settings.privacyEmail || '',
          supportEmail: settings.supportEmail || '',
          governingLaw: settings.governingLaw || '',
        });
        setIsConfigured(Boolean(settings.isConfigured));
        setUpdatedAt(settings.updatedAt || null);
        setUpdatedBy(settings.updatedBy || '');
      }
    } catch (err) {
      toast.error('Failed to load platform settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const change = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const abnValid = isValidAbn(form.abn);
  const abnWithoutName = Boolean(form.abn.trim()) && !form.legalEntityName.trim();

  const handleSave = async () => {
    if (!abnValid) {
      toast.error('ABN must be 11 digits and pass the ATO checksum');
      return;
    }
    if (abnWithoutName) {
      toast.error('Enter the legal entity name that holds this ABN');
      return;
    }

    setSaving(true);
    try {
      const response = await adminApi.updatePlatformSettings(form);
      const settings = response.data?.settings;
      setIsConfigured(Boolean(settings?.isConfigured));
      setUpdatedAt(settings?.updatedAt || null);
      setUpdatedBy(settings?.updatedBy || '');
      toast.success('Platform legal details saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save platform settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="w-6 h-6" />
            Legal Entity
          </h1>
          <p className="text-gray-500 mt-1">
            The entity that operates Practice Manager Pro. These details appear on the public
            Privacy Policy and Terms of Service.
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? (
            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Status banner — makes it obvious the legal pages are running without an
          operator entity, which is the current state until PMP is registered. */}
      {isConfigured ? (
        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">
            <p className="font-medium">Operator entity is published</p>
            <p className="mt-1">
              The Privacy Policy and Terms of Service now name{' '}
              <strong>{form.legalEntityName}</strong> (ABN {formatAbn(form.abn)}).
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">No operator entity set</p>
            <p className="mt-1">
              The legal pages currently omit the "operated by ..." line entirely rather than name
              an entity that does not exist. Fill in the legal entity name and ABN once PMP is
              registered, and both pages pick them up immediately — no redeploy needed.
            </p>
          </div>
        </div>
      )}

      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">Registered Entity</h2>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Legal entity name
            </label>
            <input
              type="text"
              value={form.legalEntityName}
              onChange={(e) => change('legalEntityName', e.target.value)}
              placeholder="e.g. Practice Manager Pro Pty Ltd"
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Exactly as registered with ASIC. Leave blank until the company exists.
            </p>
            {abnWithoutName && (
              <p className="text-xs text-red-600 mt-1">
                Required when an ABN is set — an ABN with no entity name would render a dangling
                line on the legal pages.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trading name</label>
            <input
              type="text"
              value={form.tradingName}
              onChange={(e) => change('tradingName', e.target.value)}
              placeholder="Practice Manager Pro"
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Shown in the contact blocks.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ABN</label>
            <input
              type="text"
              value={form.abn}
              onChange={(e) => change('abn', e.target.value)}
              placeholder="11 digits"
              className="input"
            />
            {form.abn && !abnValid ? (
              <p className="text-xs text-red-600 mt-1">
                Not a valid ABN — must be 11 digits and pass the ATO checksum.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                {form.abn && abnValid
                  ? `Will display as ${formatAbn(form.abn)}`
                  : 'Validated against the ATO checksum before saving.'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ACN <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={form.acn}
              onChange={(e) => change('acn', e.target.value)}
              placeholder="9 digits"
              className="input"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Registered address
            </label>
            <input
              type="text"
              value={form.registeredAddress}
              onChange={(e) => change('registeredAddress', e.target.value)}
              placeholder="e.g. Sydney, NSW, Australia"
              className="input"
            />
          </div>
        </div>
      </div>

      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">Contact &amp; Jurisdiction</h2>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Privacy contact email
            </label>
            <input
              type="email"
              value={form.privacyEmail}
              onChange={(e) => change('privacyEmail', e.target.value)}
              placeholder="privacy@practicemanagerpro.com.au"
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Quoted in the Privacy Policy.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Support contact email
            </label>
            <input
              type="email"
              value={form.supportEmail}
              onChange={(e) => change('supportEmail', e.target.value)}
              placeholder="support@practicemanagerpro.com.au"
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">Quoted in the Terms of Service.</p>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Governing law</label>
            <input
              type="text"
              value={form.governingLaw}
              onChange={(e) => change('governingLaw', e.target.value)}
              placeholder="New South Wales, Australia"
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Used in the Governing Law clause of the Terms of Service.
            </p>
          </div>
        </div>
      </div>

      {updatedAt && (
        <p className="text-xs text-gray-500">
          Last updated {new Date(updatedAt).toLocaleString('en-AU')}
          {updatedBy ? ` by ${updatedBy}` : ''}.
        </p>
      )}
    </div>
  );
};

export default LegalEntityPage;
